import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Globe } from "@phosphor-icons/react"
import { useState } from "react"
import { toast } from "sonner"

interface TrialApplicationFormProps {
  formType?: string
  successMessage?: string
  dark?: boolean
}

export function TrialApplicationForm({
  formType = "Trial Application",
  successMessage = "Application submitted successfully! We'll be in touch soon.",
  dark = false,
}: TrialApplicationFormProps) {
  const [country, setCountry] = useState<"nigeria" | "india">("india")
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    age: "",
    position: "",
    experience: ""
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          country,
          formType
        })
      })

      if (!response.ok) throw new Error('Failed to send')

      toast.success(successMessage)

      setFormData({
        name: "",
        email: "",
        phone: "",
        age: "",
        position: "",
        experience: ""
      })
    } catch {
      toast.error("Failed to submit application. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputClass = dark
    ? "bg-navy border-ivory/20 text-ivory placeholder:text-ivory/40"
    : "border-navy/20"

  const labelClass = dark
    ? "text-ivory"
    : "text-navy font-semibold text-sm"

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Country Selection */}
      <div className="space-y-2">
        <Label htmlFor="trial-country" className={`flex items-center gap-2 ${labelClass}`}>
          <Globe className="w-4 h-4 text-gold" />
          Country *
        </Label>
        <Select value={country} onValueChange={(value: "nigeria" | "india") => setCountry(value)}>
          <SelectTrigger id="trial-country" className={`h-12 ${inputClass}`}>
            <SelectValue placeholder="Select your country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nigeria">🇳🇬 Nigeria</SelectItem>
            <SelectItem value="india">🇮🇳 India</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="trial-name" className={labelClass}>Full Name *</Label>
        <Input
          id="trial-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          placeholder="Enter your full name"
          className={inputClass}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="trial-email" className={labelClass}>Email Address *</Label>
        <Input
          id="trial-email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          placeholder="your.email@example.com"
          className={inputClass}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="trial-phone" className={labelClass}>Phone Number *</Label>
        <Input
          id="trial-phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          required
          placeholder={country === "nigeria" ? "+234 XXX XXX XXXX" : "+91 XXXXX XXXXX"}
          className={inputClass}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="trial-age" className={labelClass}>Age Group *</Label>
        <Select value={formData.age} onValueChange={(value) => setFormData({ ...formData, age: value })} required>
          <SelectTrigger id="trial-age" className={inputClass}>
            <SelectValue placeholder="Select age group" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="6-9">6-9 years</SelectItem>
            <SelectItem value="10-12">10-12 years</SelectItem>
            <SelectItem value="13-15">13-15 years</SelectItem>
            <SelectItem value="16-18">16-18 years</SelectItem>
            <SelectItem value="18+">18+ years</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="trial-position" className={labelClass}>Preferred Position *</Label>
        <Select value={formData.position} onValueChange={(value) => setFormData({ ...formData, position: value })} required>
          <SelectTrigger id="trial-position" className={inputClass}>
            <SelectValue placeholder="Select position" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="goalkeeper">Goalkeeper</SelectItem>
            <SelectItem value="defender">Defender</SelectItem>
            <SelectItem value="midfielder">Midfielder</SelectItem>
            <SelectItem value="forward">Forward</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="trial-experience" className={labelClass}>Playing Experience</Label>
        <Textarea
          id="trial-experience"
          value={formData.experience}
          onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
          placeholder="Tell us about your football experience, teams played for, achievements, etc."
          rows={4}
          className={inputClass}
        />
      </div>

      <Button
        type="submit"
        className="w-full bg-gold text-navy hover:bg-gold/90 font-medium"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Submitting..." : "Submit Application"}
      </Button>
    </form>
  )
}
